// src/pages/operations-overview/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../utils/supabase";
import LabelPrintModal from "./components/LabelPrintModal.jsx";
import TechOperationsModal from "./components/TechOperationsModal.jsx";
import {
  buildOperationsListPath,
  readOperationsListFilters,
} from "../../utils/operations-view";

/* --- HELPERS --- */
function formatDateEU(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

const pad3 = (n) => String(n ?? "").padStart(3, "0");

const SERVICE_ORDER_STATUSES = [
  { value: "delivered_to_lab", label: "Entregado a laboratorio (ADM-Paola)", icon: "📥" },
  { value: "calibration_finished", label: "Calibración finalizada (LAB-Técn. asignado)", icon: "🧪" },
  {
    value: "certificates_finished_controlled",
    label: "Certificados finalizados y controlados (LAB-Diego)",
    icon: "📄",
  },
  { value: "uploaded_for_remito", label: "Subido para remitar (LAB-Diego)", icon: "📤" },
  { value: "remito_generated", label: "Remito generado (ADM-Paola)", icon: "🧾" },
  { value: "client_notified", label: "Cliente avisado (ADM-Paola/Dali)", icon: "📞" },
];

const LEGACY_STATUS_TO_NEW = {
  pending: "delivered_to_lab",
  in_progress: "calibration_finished",
  completed: "client_notified",
  blocked: "uploaded_for_remito",
};

function normalizeServiceOrderStatus(status) {
  const s = String(status || "").trim();
  if (!s) return "delivered_to_lab";
  if (SERVICE_ORDER_STATUSES.some((x) => x.value === s)) return s;
  return LEGACY_STATUS_TO_NEW[s] || "delivered_to_lab";
}

function priorityConfig(priority) {
  const map = {
    low: {
      label: "Baja",
      className:
        "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200",
      icon: "🟢",
    },
    medium: {
      label: "Media",
      className: "bg-sky-500/10 border border-sky-500/40 text-sky-200",
      icon: "🔵",
    },
    high: {
      label: "Alta",
      className:
        "bg-amber-500/10 border border-amber-500/40 text-amber-200",
      icon: "🟠",
    },
    urgent: {
      label: "Urgente",
      className: "bg-red-500/10 border border-red-500/40 text-red-200",
      icon: "🔥",
    },
  };
  return (
    map[priority] || {
      label: priority || "—",
      className: "bg-white/10 border border-white/30 text-white",
      icon: "•",
    }
  );
}

function statusConfig(status) {
  const s = normalizeServiceOrderStatus(status);
  const map = {
    delivered_to_lab: {
      label: "Entregado a laboratorio (ADM-Paola)",
      className:
        "bg-slate-500/10 border border-slate-500/40 text-slate-200",
      icon: "📥",
    },
    calibration_finished: {
      label: "Calibración finalizada (LAB-Técn. asignado)",
      className:
        "bg-indigo-500/10 border border-indigo-500/40 text-indigo-200",
      icon: "🧪",
    },
    certificates_finished_controlled: {
      label: "Certificados finalizados y controlados (LAB-Diego)",
      className: "bg-violet-500/10 border border-violet-500/40 text-violet-200",
      icon: "📄",
    },
    uploaded_for_remito: {
      label: "Subido para remitar (LAB-Diego)",
      className: "bg-amber-500/10 border border-amber-500/40 text-amber-200",
      icon: "📤",
    },
    remito_generated: {
      label: "Remito generado (ADM-Paola)",
      className: "bg-cyan-500/10 border border-cyan-500/40 text-cyan-200",
      icon: "🧾",
    },
    client_notified: {
      label: "Cliente avisado (ADM-Paola/Dali)",
      className:
        "bg-emerald-500/10 border border-emerald-500/40 text-emerald-200",
      icon: "📞",
    },
  };
  return (
    map[s] || {
      label: s || "—",
      className: "bg-white/10 border border-white/30 text-white",
      icon: "•",
    }
  );
}

export default function TechnicianDashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = useMemo(() => readOperationsListFilters(searchParams), []);

  const [technicians, setTechnicians] = useState([]);
  const [selectedTechId, setSelectedTechId] = useState(initialFilters.technicianId);
  const [loadingTechs, setLoadingTechs] = useState(true);

  const [isCurrentUserTechnician, setIsCurrentUserTechnician] = useState(false);
  const [isCurrentUserLabManager, setIsCurrentUserLabManager] = useState(false);
  const [canSelectTechnicians, setCanSelectTechnicians] = useState(false);
  const [currentTechName, setCurrentTechName] = useState("");
  const [authReady, setAuthReady] = useState(false);

  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orders, setOrders] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [search, setSearch] = useState(initialFilters.search);

  // Etiquetas
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [labelModalTitle, setLabelModalTitle] = useState("Imprimir etiqueta");
  const [labelInitialLabels, setLabelInitialLabels] = useState(null);
  const [printingPoId, setPrintingPoId] = useState(null);

  // Modal técnico (estado)
  const [techModalOpen, setTechModalOpen] = useState(false);
  const [techModalOrder, setTechModalOrder] = useState(null);
  const [ordersReloadTick, setOrdersReloadTick] = useState(0);

  // 0) Detectar usuario actual (técnico) + resolver technician_id
  useEffect(() => {
    let cancelled = false;

    const checkCurrentUser = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("No hay una sesión autenticada.");

        // Perfil (nombre + rol)
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("full_name, role")
          .eq("id", user.id)
          .single();

        if (profileError) throw profileError;
        if (cancelled) return;

        const profileName = profile?.full_name || user.email || "Técnico";
        setCurrentTechName(profileName);

        const isTechRole = profile?.role === "technician";
        const isLabManagerRole = profile?.role === "lab_manager" || profile?.role === "admin";

        let techId = null;

        // 1) RPC current_technician_id()
        const { data: rpcTechId, error: rpcErr } = await supabase.rpc(
          "current_technician_id"
        );
        if (!rpcErr && rpcTechId) {
          techId = rpcTechId;
        } else {
          // 2) tabla technicians por user_id (puede estar bloqueada por RLS para técnicos)
          const { data: techData, error: techError } = await supabase
            .from("technicians")
            .select("id, full_name, active")
            .eq("user_id", user.id)
            .eq("active", true)
            .maybeSingle();

          if (!techError && techData?.id) {
            techId = techData.id;
            if (techData.full_name) setCurrentTechName(techData.full_name);
          }
        }

        // 3) fallback: si tu sistema usa technician_id = auth.uid()
        if (isTechRole && !techId) {
          const fallbackId = user.id;
          const { data: probeRows, error: probeErr } = await supabase
            .from("service_orders")
            .select("id")
            .eq("technician_id", fallbackId)
            .limit(1);

          if (!probeErr && (probeRows?.length ?? 0) > 0) {
            techId = fallbackId;
          }
        }

        setIsCurrentUserLabManager(isLabManagerRole);
        setCanSelectTechnicians(!isTechRole);

        // Una cuenta con rol superior puede estar vinculada a una ficha técnica.
        // El rol efectivo manda: solo technician queda limitado a sus propias OS.
        const isTechSession = isTechRole;

        if (isTechSession) {
          setIsCurrentUserTechnician(true);

          if (techId) {
            setSelectedTechId(techId);
            setTechnicians([{ id: techId, full_name: profileName, active: true }]);
            setErrorMsg("");
          } else {
            setSelectedTechId("");
            setTechnicians([{ id: "", full_name: profileName, active: true }]);
            setErrorMsg(
              "Tu cuenta no está vinculada a una ficha técnica activa. Contactá a un administrador."
            );
          }
        } else {
          setIsCurrentUserTechnician(false);
        }
      } catch (err) {
        console.error("[OperationsOverview] checkCurrentUser error:", err);
        if (!cancelled) {
          setIsCurrentUserTechnician(false);
          setIsCurrentUserLabManager(false);
          setCanSelectTechnicians(false);
          setSelectedTechId("");
          setTechnicians([]);
          setOrders([]);
          setErrorMsg(
            "No se pudo identificar tu cuenta. Recargá la página o volvé a iniciar sesión."
          );
        }
      } finally {
        if (!cancelled) setAuthReady(true);
      }
    };

    checkCurrentUser();

    return () => {
      cancelled = true;
    };
  }, []);

  // 1) Técnicos activos (solo para NO técnicos)
  useEffect(() => {
    if (!authReady) return undefined;

    let cancelled = false;

    const loadTechs = async () => {
      if (isCurrentUserTechnician || !canSelectTechnicians) {
        setLoadingTechs(false);
        return;
      }

      try {
        setLoadingTechs(true);
        setErrorMsg("");

        const { data, error } = await supabase
          .from("technicians")
          .select("id, full_name, active")
          .eq("active", true)
          .order("full_name", { ascending: true });

        if (error) throw error;
        if (cancelled) return;

        const activeTechnicians = data || [];
        setTechnicians(activeTechnicians);
        if (activeTechnicians.length > 0) {
          setSelectedTechId((prev) =>
            activeTechnicians.some((technician) => technician.id === prev)
              ? prev
              : activeTechnicians[0].id
          );
        }
      } catch (err) {
        console.error("[OperationsOverview] loadTechs error:", err);
        if (!cancelled) {
          setErrorMsg(err.message || "No se pudieron cargar los técnicos");
        }
      } finally {
        if (!cancelled) setLoadingTechs(false);
      }
    };

    loadTechs();

    return () => {
      cancelled = true;
    };
  }, [authReady, canSelectTechnicians, isCurrentUserTechnician]);

  // Mantener técnico y búsqueda en la URL. Así sobreviven al entrar en una OS,
  // volver, recargar la página o usar los botones Atrás/Adelante del navegador.
  useEffect(() => {
    if (!authReady) return;
    const nextPath = buildOperationsListPath({ technicianId: selectedTechId, search });
    const nextQuery = nextPath.includes('?') ? nextPath.split('?')[1] : '';
    if (nextQuery !== searchParams.toString()) {
      setSearchParams(nextQuery, { replace: true });
    }
  }, [authReady, search, searchParams, selectedTechId, setSearchParams]);

  // 2) Cargar trabajos (OC) asignados a este técnico (por po_id)
  useEffect(() => {
    if (!authReady) return undefined;

    if (!selectedTechId) {
      setOrders([]);
      setLoadingOrders(false);
      return undefined;
    }

    let cancelled = false;

    const loadOrders = async () => {
      try {
        setLoadingOrders(true);
        setErrorMsg("");

        // IDs de OS asignadas
        const { data: soIds, error: soIdsError } = await supabase
          .from("service_orders")
          .select("id")
          .eq("technician_id", selectedTechId);

        if (soIdsError) throw soIdsError;
        if (cancelled) return;

        if (!soIds || soIds.length === 0) {
          setOrders([]);
          return;
        }

        const serviceOrderIds = soIds.map((r) => r.id);

        // Fuente de verdad de estado: service_orders.status
        const { data: soRows, error: soError } = await supabase
          .from("service_orders")
          .select("id, code, status, created_at, po_id")
          .in("id", serviceOrderIds);

        if (soError) throw soError;
        if (cancelled) return;

        if (!soRows || soRows.length === 0) {
          setOrders([]);
          return;
        }

        // agrupar por po_id
        const groupsByPo = new Map();
        for (const so of soRows) {
          if (!so.po_id) continue;
          const ex = groupsByPo.get(so.po_id);
          if (!ex) {
            groupsByPo.set(so.po_id, {
              po_id: so.po_id,
              soCodes: [so.code],
              serviceOrderIds: [so.id],
              latest_status: so.status || null,
              instruments_count: 1,
              created_at: so.created_at,
            });
          } else {
            ex.soCodes.push(so.code);
            ex.serviceOrderIds.push(so.id);
            ex.latest_status = ex.latest_status || so.status || null;
            ex.instruments_count += 1;
            if (
              ex.created_at &&
              new Date(so.created_at).getTime() < new Date(ex.created_at).getTime()
            ) {
              ex.created_at = so.created_at;
            }
          }
        }

        const poIds = Array.from(groupsByPo.keys());
        if (poIds.length === 0) {
          setOrders([]);
          return;
        }

        // metadata PO (incluye ops_status)
        const { data: poRows, error: poError } = await supabase
          .from("v_purchase_orders")
          .select(
            `
            id,
            code,
            client_name,
            ops_priority,
            ops_status,
            ops_due_date,
            ops_notes
          `
          )
          .in("id", poIds);

        if (poError) throw poError;
        if (cancelled) return;

        const poMetaById = Object.fromEntries((poRows || []).map((po) => [po.id, po]));

        const workOrders = poIds
          .map((poId) => {
            const group = groupsByPo.get(poId);
            const meta = poMetaById[poId] || {};

            return {
              id: poId,
              po_code: meta.code || "",
              client_name: meta.client_name || "—",
              ops_priority: meta.ops_priority || "medium",
              // Priorizar status real de service_orders por sobre ops_status de PO.
              ops_status: normalizeServiceOrderStatus(group?.latest_status || meta.ops_status),
              ops_due_date: meta.ops_due_date || null,
              ops_notes: meta.ops_notes || "",
              instruments_count: group?.instruments_count || 0,
              created_at: group?.created_at || null,
              soCodes: group?.soCodes || [],
              service_order_id: group?.serviceOrderIds?.[0] || null,
            };
          })
          .sort((a, b) => {
            const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
            const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
            return bDate - aDate;
          });

        setOrders(workOrders);
      } catch (err) {
        console.error("[OperationsOverview] loadOrders error:", err);
        if (!cancelled) {
          setErrorMsg(err.message || "No se pudieron cargar las órdenes");
          setOrders([]);
        }
      } finally {
        if (!cancelled) setLoadingOrders(false);
      }
    };

    loadOrders();

    return () => {
      cancelled = true;
    };
  }, [authReady, selectedTechId, ordersReloadTick]);

  // KPI
  const normalizeSearchText = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[\s-]+/g, "")
      .trim();

  const filteredOrders = useMemo(() => {
    const q = normalizeSearchText(search);
    if (!q) return orders;

    const qDigits = q.replace(/\D+/g, "");

    const matchesText = (value) => {
      const normalized = normalizeSearchText(value);
      if (!normalized) return false;
      if (normalized.includes(q)) return true;

      // Si el usuario escribe solo números (ej. 6614, 6614002), matchear contra números del código.
      if (qDigits) {
        const normalizedDigits = normalized.replace(/\D+/g, "");
        if (normalizedDigits.includes(qDigits)) return true;
      }
      return false;
    };

    return (orders || []).filter((o) => {
      const poCode = o?.po_code || "";
      const poFallback = o?.oc_number || o?.po_number || "";
      const soCodes = Array.isArray(o?.soCodes) ? o.soCodes : [];

      if (matchesText(poCode) || matchesText(poFallback)) return true;
      return soCodes.some((soCode) => matchesText(soCode));
    });
  }, [orders, search]);

  const kpiSource = useMemo(
    () => (normalizeSearchText(search) ? filteredOrders : orders),
    [orders, filteredOrders, search]
  );

  const kpis = useMemo(() => {
    const total = kpiSource.length;
    const deliveredToLab = kpiSource.filter(
      (o) => normalizeServiceOrderStatus(o.ops_status) === "delivered_to_lab"
    ).length;
    const calibrationFinished = kpiSource.filter(
      (o) => normalizeServiceOrderStatus(o.ops_status) === "calibration_finished"
    ).length;
    const certsControlled = kpiSource.filter(
      (o) =>
        normalizeServiceOrderStatus(o.ops_status) ===
        "certificates_finished_controlled"
    ).length;
    const uploadedForRemito = kpiSource.filter(
      (o) => normalizeServiceOrderStatus(o.ops_status) === "uploaded_for_remito"
    ).length;
    const remitoGenerated = kpiSource.filter(
      (o) => normalizeServiceOrderStatus(o.ops_status) === "remito_generated"
    ).length;
    const clientNotified = kpiSource.filter(
      (o) => normalizeServiceOrderStatus(o.ops_status) === "client_notified"
    ).length;

    return {
      total,
      deliveredToLab,
      calibrationFinished,
      certsControlled,
      uploadedForRemito,
      remitoGenerated,
      clientNotified,
    };
  }, [kpiSource]);

  // Modal manual etiquetas
  const openManualLabelModal = () => {
    setLabelModalTitle("Imprimir etiqueta");
    setLabelInitialLabels(null);
    setLabelModalOpen(true);
  };

  const openTechStatusModal = (order) => {
    if (!order?.id) return;
    setTechModalOrder(order);
    setTechModalOpen(true);
  };

  const handleTechModalSaved = (payload) => {
    const poId = techModalOrder?.id;
    const nextStatus = normalizeServiceOrderStatus(payload?.status);
    if (poId) {
      setOrders((prev) =>
        prev.map((o) => (o.id === poId ? { ...o, ops_status: nextStatus } : o))
      );
    }
    setTechModalOpen(false);
    setTechModalOrder(null);
    setOrdersReloadTick((v) => v + 1);
  };

  const handleRefreshOrders = () => {
    if (!selectedTechId || loadingOrders) return;
    setOrdersReloadTick((v) => v + 1);
  };

  // Imprimir etiquetas por OC
  const handlePrintLabelsForOrder = async (order) => {
    const poId = order?.id;
    if (!poId) return;

    setErrorMsg("");
    setPrintingPoId(poId);

    try {
      const { data: soRows, error: soErr } = await supabase
        .from("v_service_orders_enriched")
        .select(
          "service_order_id, instrument_id, code, instrument_tag, instrument_serial, created_at"
        )
        .eq("po_id", poId)
        .order("created_at", { ascending: true });

      if (soErr) throw soErr;

      const serviceOrderIds = Array.from(
        new Set((soRows || []).map((r) => r.service_order_id).filter(Boolean))
      );

      if (serviceOrderIds.length === 0) {
        setLabelModalTitle(`Imprimir etiquetas — ${order.po_code || "OC"}`);
        setLabelInitialLabels([]);
        setLabelModalOpen(true);
        return;
      }

      const { data: soTableRows, error: soTableErr } = await supabase
        .from("service_orders")
        .select("id, base_number, base_seq")
        .in("id", serviceOrderIds);

      if (soTableErr) throw soTableErr;

      const metaBySoId = new Map((soTableRows || []).map((r) => [r.id, r]));

      const rowsSorted = [...(soRows || [])].sort((a, b) => {
        const sa = metaBySoId.get(a.service_order_id)?.base_seq ?? 999999;
        const sb = metaBySoId.get(b.service_order_id)?.base_seq ?? 999999;
        return sa - sb;
      });

      const labels = rowsSorted.map((r, idx) => {
        const meta = metaBySoId.get(r.service_order_id) || {};
        const baseNumber = meta.base_number;
        const baseSeq = meta.base_seq ?? (idx + 1);
        const seq3 = pad3(baseSeq);
        const cert = baseNumber ? `${baseNumber}${seq3}` : "";

        const sourceTag = (r.instrument_tag || "").trim();
        const sourceSerial = (r.instrument_serial || "").trim();

        return {
          cert,
          idEquipo: sourceTag,
          fechaCal: "",
          fechaVto: "",
          selected: true,
          _usesAutomaticIdentifier: true,
          sourceTag,
          sourceSerial,
          instrumentId: r.instrument_id,
          instrumentLabel: r.code || `Etiqueta ${idx + 1}`,
        };
      });

      setLabelModalTitle(`Imprimir etiquetas — ${order.po_code || "OC"}`);
      setLabelInitialLabels(labels);
      setLabelModalOpen(true);
    } catch (err) {
      console.error("[OperationsOverview] handlePrintLabelsForOrder error:", err);
      setErrorMsg(err.message || "No se pudieron preparar las etiquetas");
    } finally {
      setPrintingPoId(null);
    }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold">
            {isCurrentUserLabManager ? "OS del laboratorio / Trabajos asignados" : "Mis OS / Trabajos asignados"}
          </h1>
          <p className="text-sm text-slate-400">
            {isCurrentUserLabManager
              ? "Visualizá las órdenes de servicio de todos los técnicos, agrupadas por trabajo (OC)."
              : "Visualizá tus órdenes de servicio agrupadas por trabajo (OC) con sus prioridades."}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            type="button"
            className="rounded-md border border-amber-500/60 px-3 py-1.5 text-sm text-amber-100 hover:bg-amber-500/10 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleRefreshOrders}
            disabled={!selectedTechId || loadingOrders}
            title="Volver a consultar los trabajos asignados"
          >
            {loadingOrders ? "↻ Actualizando…" : "↻ Actualizar trabajos"}
          </button>

          <button
            className="rounded-md border border-emerald-500/60 px-3 py-1.5 text-sm text-emerald-100 hover:bg-emerald-500/10"
            onClick={openManualLabelModal}
            title="Abrir impresor de etiquetas (manual)"
          >
            🏷️ Imprimir etiqueta
          </button>

          <span className="text-sm text-slate-300">Técnico:</span>

          {!authReady ? (
            <span className="text-sm text-slate-400">Cargando…</span>
          ) : isCurrentUserTechnician ? (
            <div className="rounded-md border border-slate-600 bg-slate-900 px-3 py-1.5 text-sm text-slate-100">
              {currentTechName || "Técnico"}
            </div>
          ) : loadingTechs ? (
            <span className="text-sm text-slate-400">Cargando…</span>
          ) : technicians.length === 0 ? (
            <span className="text-sm text-slate-400">No hay técnicos activos.</span>
          ) : (
            <select
              className="rounded-md border border-slate-600 bg-slate-900 px-3 py-1.5 text-sm text-slate-100"
              value={selectedTechId || ""}
              onChange={(e) => setSelectedTechId(e.target.value)}
            >
              <option value="">Seleccionar técnico…</option>
              {technicians.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.full_name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Buscador */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por Nº OC / Nº OS…"
            className="w-full rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/40"
          />
          {search ? (
            <button
              className="rounded-md border border-slate-500 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
              onClick={() => setSearch("")}
              title="Limpiar búsqueda"
            >
              Limpiar ✕
            </button>
          ) : null}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <KpiCard label="Trabajos (OC) asignados" value={kpis.total} />
        <KpiCard label="Entregado a laboratorio" value={kpis.deliveredToLab} tone="slate" />
        <KpiCard label="Calibración finalizada" value={kpis.calibrationFinished} tone="indigo" />
        <KpiCard
          label="Certificados finalizados y controlados"
          value={kpis.certsControlled}
          tone="violet"
        />
        <KpiCard label="Subido para remitar" value={kpis.uploadedForRemito} tone="amber" />
        <KpiCard label="Remito generado" value={kpis.remitoGenerated} tone="cyan" />
        <KpiCard label="Cliente avisado" value={kpis.clientNotified} tone="emerald" />
      </div>

      {/* Error */}
      {errorMsg && (
        <div className="rounded-md border border-red-500/50 bg-red-500/10 px-3 py-2 text-sm text-red-100">
          {errorMsg}
        </div>
      )}

      {/* Lista */}
      <div className="space-y-2">
        {loadingOrders ? (
          <div className="rounded-lg border border-white/10 p-4">Cargando trabajos…</div>
        ) : !selectedTechId ? (
          <div className="rounded-lg border border-white/10 p-4 text-sm text-slate-300">
            {isCurrentUserTechnician
              ? "No se pudo resolver tu técnico (technician_id)."
              : "Seleccioná un técnico para ver sus trabajos asignados."}
          </div>
        ) : orders.length === 0 ? (
          <div className="rounded-lg border border-white/10 p-4">
            No hay trabajos asignados a este técnico.
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="rounded-lg border border-white/10 p-4">
            No se encontraron órdenes con ese número.
          </div>
        ) : (
          filteredOrders.map((o) => (
            <WorkOrderCard
              key={o.id}
              order={o}
              onOpen={() => navigate(`/ops/service-orders/${o.id}`, {
                state: {
                  returnTo: buildOperationsListPath({ technicianId: selectedTechId, search }),
                },
              })}
              onPrint={() => handlePrintLabelsForOrder(o)}
              printing={printingPoId === o.id}
              canEditStatus={isCurrentUserTechnician || isCurrentUserLabManager}
              onOpenStatusModal={() => openTechStatusModal(o)}
            />
          ))
        )}
      </div>

      {/* Modal impresión */}
      <LabelPrintModal
        open={labelModalOpen}
        onClose={() => setLabelModalOpen(false)}
        defaultSize="40.5x18"
        title={labelModalTitle}
        initialLabels={labelInitialLabels}
      />

      <TechOperationsModal
        open={techModalOpen}
        onClose={() => {
          setTechModalOpen(false);
          setTechModalOrder(null);
        }}
        po={techModalOrder}
        onSaved={handleTechModalSaved}
      />
    </div>
  );
}

/* --- UI --- */
function KpiCard({ label, value, tone = "slate" }) {
  const toneMap = {
    slate: "border-slate-500/40 bg-slate-500/5 text-slate-100",
    indigo: "border-indigo-500/40 bg-indigo-500/5 text-indigo-100",
    violet: "border-violet-500/40 bg-violet-500/5 text-violet-100",
    amber: "border-amber-500/40 bg-amber-500/5 text-amber-100",
    cyan: "border-cyan-500/40 bg-cyan-500/5 text-cyan-100",
    emerald: "border-emerald-500/40 bg-emerald-500/5 text-emerald-100",
    red: "border-red-500/40 bg-red-500/5 text-red-100",
  };
  const classes = toneMap[tone] || toneMap.slate;

  return (
    <div className={`rounded-lg border px-3 py-2 ${classes}`}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </div>
  );
}

function WorkOrderCard({
  order,
  onOpen,
  onPrint,
  printing,
  canEditStatus,
  onOpenStatusModal,
}) {
  const priCfg = priorityConfig(order.ops_priority);
  const stCfg = statusConfig(order.ops_status);
  const dueLabel = order.ops_due_date ? formatDateEU(order.ops_due_date) : null;

  const firstOs = order.soCodes?.[0];
  const osSummary =
    order.soCodes && order.soCodes.length > 1
      ? `${firstOs} (+${order.soCodes.length - 1} más)`
      : firstOs || "—";

  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-3 flex flex-col gap-2 md:flex-row md:items-center">
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">{order.po_code || "OC sin código"}</span>
          <span className="text-xs text-slate-300">{order.client_name}</span>
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-2">
          {/* Prioridad */}
          <span
            className={
              "px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 " +
              priCfg.className
            }
          >
            <span>{priCfg.icon}</span>
            <span>{priCfg.label}</span>
          </span>

          {/* Estado */}
          <span
            className={
              "px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 " +
              stCfg.className
            }
          >
            <span>{stCfg.icon}</span>
            <span>{stCfg.label}</span>
          </span>

          {/* Vencimiento */}
          {dueLabel && (
            <span className="px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 bg-slate-500/10 border border-slate-500/40 text-slate-200">
              <span>📅</span>
              <span>Vence: {dueLabel}</span>
            </span>
          )}

          {/* Cantidad instrumentos */}
          <span className="px-2 py-0.5 rounded-full text-xs inline-flex items-center gap-1 bg-violet-500/10 border border-violet-500/40 text-violet-200">
            <span>🧪</span>
            <span>{order.instruments_count} instrumentos</span>
          </span>
        </div>

        {osSummary && <div className="mt-1 text-xs text-slate-300">OS: {osSummary}</div>}

        {order.ops_notes && (
          <div className="mt-1 text-xs text-slate-300">Nota: {order.ops_notes}</div>
        )}
      </div>

      <div className="flex items-center gap-2 md:ml-4">
        <button
          className="px-3 py-1.5 rounded-md border border-sky-500/60 text-sky-100 hover:bg-sky-500/10 text-sm disabled:opacity-60"
          onClick={onPrint}
          disabled={printing}
          title="Imprimir todas las etiquetas de la OC"
        >
          {printing ? "Preparando…" : "Imprimir etiquetas"}
        </button>

        <button
          className="px-3 py-1.5 rounded-md border border-emerald-500/60 text-emerald-100 hover:bg-emerald-500/10 text-sm"
          onClick={onOpen}
          title="Trabajar los instrumentos uno por uno (calibrar, rechazar, pausar…)"
        >
          Ver instrumentos
        </button>
      </div>
    </div>
  );
}
